// src/utils/blockUtils.js
// Utilities for parsing and managing text blocks

/**
 * Parse journal content into time-based blocks
 * @param {string} content - Journal content with [HH:MM] markers
 * @returns {Array} Array of { timeOfDay: "HHmm", text: string }
 */
export function parseContentIntoBlocks(content) {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const blocks = [];
  
  // Regex to match [HH:MM] or [H:MM] patterns
  const timeMarkerRegex = /\[(\d{1,2}):(\d{2})\]/g;
  
  // Find all time markers and their positions
  const markers = [];
  let match;
  while ((match = timeMarkerRegex.exec(content)) !== null) {
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    markers.push({
      position: match.index,
      endPosition: match.index + match[0].length,
      timeOfDay: `${hours}${minutes}`,
      fullMatch: match[0]
    });
  }

  // If no markers found, return entire content as a single block with no time
  if (markers.length === 0) {
    const trimmedContent = content.trim();
    if (trimmedContent) {
      return [{
        timeOfDay: '', // No time specified
        text: trimmedContent
      }];
    }
    return [];
  }

  // Extract text between markers
  for (let i = 0; i < markers.length; i++) {
    const currentMarker = markers[i];
    const nextMarker = markers[i + 1];
    
    // Get text after current marker until next marker (or end of content)
    const startPos = currentMarker.endPosition;
    const endPos = nextMarker ? nextMarker.position : content.length;
    const text = content.substring(startPos, endPos).trim();
    
    if (text) {
      blocks.push({
        timeOfDay: currentMarker.timeOfDay,
        text: text
      });
    }
  }

  return blocks;
}

/**
 * Reconstruct content from blocks
 * @param {Array} blocks - Array of block objects with timeOfDay and text
 * @returns {string} Reconstructed content with [HH:MM] markers
 */
export function reconstructContentFromBlocks(blocks) {
  if (!blocks || blocks.length === 0) {
    return '';
  }

  return blocks.map(block => {
    if (block.timeOfDay && block.timeOfDay.length === 4) {
      const hours = block.timeOfDay.substring(0, 2);
      const minutes = block.timeOfDay.substring(2, 4);
      return `[${hours}:${minutes}] ${block.text}`;
    } else {
      // Block without time marker
      return block.text;
    }
  }).join('\n\n');
}

/**
 * Diff blocks to determine what needs to be added, updated, or deleted
 * @param {Array} oldBlocks - Existing blocks from Firestore (with id field)
 * @param {Array} newBlocks - New blocks parsed from content (without id)
 * @returns {Object} { toAdd: [], toUpdate: [], toDelete: [] }
 */
export function diffBlocks(oldBlocks, newBlocks) {
  const toAdd = [];
  const toUpdate = [];
  const toDelete = [];

  // Create a map of old blocks by timeOfDay for quick lookup
  const oldBlockMap = new Map();
  oldBlocks.forEach(block => {
    const key = block.timeOfDay || 'no-time';
    if (!oldBlockMap.has(key)) {
      oldBlockMap.set(key, []);
    }
    oldBlockMap.get(key).push(block);
  });

  // Create a map of new blocks by timeOfDay
  const newBlockMap = new Map();
  newBlocks.forEach(block => {
    const key = block.timeOfDay || 'no-time';
    if (!newBlockMap.has(key)) {
      newBlockMap.set(key, []);
    }
    newBlockMap.get(key).push(block);
  });

  // Process new blocks - add or update
  newBlocks.forEach(newBlock => {
    const key = newBlock.timeOfDay || 'no-time';
    const oldBlocksWithSameTime = oldBlockMap.get(key) || [];
    
    if (oldBlocksWithSameTime.length > 0) {
      // Find matching old block (same time, similar text)
      const matchingOldBlock = oldBlocksWithSameTime.find(oldBlock => 
        oldBlock.text === newBlock.text
      );
      
      if (matchingOldBlock) {
        // Text hasn't changed, no update needed
        // Remove from oldBlockMap so we don't delete it later
        const blocks = oldBlockMap.get(key);
        const index = blocks.indexOf(matchingOldBlock);
        if (index > -1) {
          blocks.splice(index, 1);
        }
      } else {
        // Text changed, update the first matching block
        const blockToUpdate = oldBlocksWithSameTime[0];
        toUpdate.push({
          id: blockToUpdate.id,
          timeOfDay: newBlock.timeOfDay,
          text: newBlock.text
        });
        
        // Remove from oldBlockMap
        const blocks = oldBlockMap.get(key);
        const index = blocks.indexOf(blockToUpdate);
        if (index > -1) {
          blocks.splice(index, 1);
        }
      }
    } else {
      // No existing block with this time, add new
      toAdd.push(newBlock);
    }
  });

  // Remaining old blocks should be deleted
  oldBlockMap.forEach((blocks) => {
    blocks.forEach(block => {
      toDelete.push(block.id);
    });
  });

  return { toAdd, toUpdate, toDelete };
}

